import { RESPONDENT_ID_PARAM, RESPONDENT_ID_STORAGE_PREFIX } from '../../constants/surveyConstants';

/**
 * Durable respondent identity for surveys fielded WITHOUT a panel provider.
 *
 * The backend resumes a session only when it is handed an `external_tracking_id` it has seen
 * before; with no id it mints a fresh session on every load, so a respondent who loses their
 * localStorage entry restarts at question 1 and their partial session is orphaned. Panel surveys
 * get that id from `iid`/`psid`. Everything else has to bring its own, which is what this is.
 *
 * Resolution order is URL -> storage -> mint, so a link carrying an id always wins: that is what
 * lets a respondent move to another browser or device and still land in the same session.
 */

// The backend column is VARCHAR(255) and the value is echoed into redirect URLs, so anything
// longer or stranger than this is refused rather than passed on to produce a 400 mid-survey.
const ACCEPTABLE_URL_ID = /^[A-Za-z0-9_-]{1,100}$/;

const storageKey = (surveyId: number): string => `${RESPONDENT_ID_STORAGE_PREFIX}${surveyId}`;

const mintRespondentId = (): string => {
  // randomUUID needs a secure context. Failing to mint would mean failing to start the survey, so
  // fall back to the same shape used elsewhere in the app rather than throwing.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

export const readStoredRespondentId = (surveyId: number): string | null => {
  try {
    return localStorage.getItem(storageKey(surveyId));
  } catch {
    // private mode / quota — the survey still works, it just cannot resume after a reload
    return null;
  }
};

export const writeStoredRespondentId = (surveyId: number, id: string): void => {
  try {
    localStorage.setItem(storageKey(surveyId), id);
  } catch {
    // as above: never let storage failure stop a respondent starting
  }
};

/**
 * The id this respondent should be known by for this survey. Writes back to storage so the id
 * survives the URL being trimmed, and so a link opened on a fresh device pins the same identity.
 */
export const resolveRespondentId = (surveyId: number, urlValue: string | null): string => {
  const fromUrl = urlValue?.trim();
  if (fromUrl && ACCEPTABLE_URL_ID.test(fromUrl)) {
    writeStoredRespondentId(surveyId, fromUrl);
    return fromUrl;
  }

  const stored = readStoredRespondentId(surveyId)?.trim();
  if (stored) return stored;

  const minted = mintRespondentId();
  writeStoredRespondentId(surveyId, minted);
  return minted;
};

/** True when this survey has no panel tracking param configured, i.e. we own the identity. */
export const shouldMintRespondentId = (
  trackingParam: Record<string, string> | null | undefined,
): boolean => Object.keys(trackingParam ?? {}).length === 0;

export { RESPONDENT_ID_PARAM };
